import type { Plan } from '@otter-ai/recall-client';
import { useMemo } from 'react';
import { useSubscription } from '@/features/account-settings/shared/hooks/useSubscription';
import { useGetCurrentUserFeatures } from '@/shared/hooks/current-user/useGetCurrentUserFeatures';
import { BuiltInApps } from '@/shared/types/apps';
import type { ZoomSyncedAccount } from '../../type/apps';
import { shouldUpgradeForAdvancedZoomFeatures } from '../../utils/features';
import { showZoomReconnect } from '../../utils/zoom';
import { useSyncedAccounts } from '../useSyncedAccounts';

export type UseMoreZoomIntegrationsStatus = {
  /**
   * Show the upsell to upgrade their Otter plan to get more Zoom features.
   */
  showUpsell: boolean;
  /**
   * Show the more Zoom integrations section for advance functionality.
   */
  showMoreZoomIntegrations: boolean;
  /**
   * Data is still loading.
   */
  isLoading: boolean;
  /**
   * Does the user need to upgrade their Otter plan to get more Zoom features?
   */
  needsToUpgradeForAdvancedZoomFeatures: boolean;
};

/**
 * Find out if the user can use advanced Zoom features or if they need to upgrade their Otter plan.
 */
export function useMoreZoomIntegrationsStatus(): UseMoreZoomIntegrationsStatus {
  const { accounts } = useSyncedAccounts({
    service: BuiltInApps.ZOOM,
  });
  const { data: userFeatures } = useGetCurrentUserFeatures();
  const { subscription } = useSubscription();
  const requiresReconnect = useMemo(() => {
    return showZoomReconnect(accounts as ZoomSyncedAccount[]);
  }, [accounts]);

  if (accounts.length === 0) {
    return {
      showUpsell: false,
      showMoreZoomIntegrations: false,
      isLoading: false,
      needsToUpgradeForAdvancedZoomFeatures: false,
    };
  }

  if (!userFeatures || !subscription) {
    return {
      showUpsell: false,
      showMoreZoomIntegrations: false,
      isLoading: true,
      needsToUpgradeForAdvancedZoomFeatures: false,
    };
  }

  const needsToUpgradeForAdvancedZoomFeatures =
    shouldUpgradeForAdvancedZoomFeatures(
      userFeatures,
      subscription.plan as unknown as Plan,
    );

  const showMoreZoomIntegrations =
    !needsToUpgradeForAdvancedZoomFeatures && requiresReconnect === false;

  return {
    needsToUpgradeForAdvancedZoomFeatures,
    showUpsell: needsToUpgradeForAdvancedZoomFeatures,
    showMoreZoomIntegrations,
    isLoading: false,
  };
}
